import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AppTour.css';

/**
 * AppTour — step-by-step product tour with a real moving spotlight.
 *
 * Props:
 *   steps      — array of { target, title, body, icon, position? }
 *   onEnd      — called when tour finishes or is skipped
 *   storageKey — localStorage key to mark tour as seen
 */
export default function AppTour({ steps, onEnd, storageKey = 'app_tour_done' }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);   // viewport-relative DOMRect of target
  const [show, setShow] = useState(false);
  const tooltipRef = useRef(null);

  const current = steps[step];

  // Find the target element and measure it in viewport coords
  const measure = useCallback(() => {
    if (!current?.target) { setRect(null); return; }
    const el = document.querySelector(current.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [current]);

  // Re-measure on step change and on resize/scroll
  useEffect(() => {
    setShow(false);
    const t = setTimeout(() => { measure(); setShow(true); }, 250);
    return () => clearTimeout(t);
  }, [step, measure]);

  useEffect(() => {
    const handler = () => measure();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [measure]);

  const finish = useCallback(() => {
    localStorage.setItem(storageKey, 'true');
    setShow(false);
    setTimeout(() => onEnd?.(), 200);
  }, [storageKey, onEnd]);

  const goNext = () => {
    if (step < steps.length - 1) {
      setShow(false);
      setTimeout(() => setStep(s => s + 1), 200);
    } else {
      finish();
    }
  };

  const goPrev = () => {
    if (step > 0) {
      setShow(false);
      setTimeout(() => setStep(s => s - 1), 200);
    }
  };

  if (!current) return null;

  // ── Tooltip position ──────────────────────────────────────────────────────
  // Everything is in viewport (fixed) coordinates.
  const PAD = 14;
  const TW = 300;   // tooltip width
  const TH = 200;   // estimated tooltip height

  let ttTop = 0, ttLeft = 0;

  if (rect) {
    const pos = current.position || 'auto';
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const spaceAbove = rect.top;
    const spaceRight = window.innerWidth - (rect.left + rect.width);

    if (pos === 'right' || (pos === 'auto' && spaceRight > TW + PAD)) {
      ttTop  = rect.top + rect.height / 2 - TH / 2;
      ttLeft = rect.left + rect.width + PAD;
    } else if (pos === 'left') {
      ttTop  = rect.top + rect.height / 2 - TH / 2;
      ttLeft = rect.left - TW - PAD;
    } else if (pos === 'above' || (pos === 'auto' && spaceBelow < TH + PAD && spaceAbove > TH + PAD)) {
      ttTop  = rect.top - TH - PAD;
      ttLeft = rect.left + rect.width / 2 - TW / 2;
    } else {
      // below (default)
      ttTop  = rect.top + rect.height + PAD;
      ttLeft = rect.left + rect.width / 2 - TW / 2;
    }

    // Clamp to viewport
    ttLeft = Math.max(PAD, Math.min(ttLeft, window.innerWidth  - TW  - PAD));
    ttTop  = Math.max(PAD, Math.min(ttTop,  window.innerHeight - TH  - PAD));
  } else {
    // No target found — centre on screen
    ttTop  = window.innerHeight / 2 - TH / 2;
    ttLeft = window.innerWidth  / 2 - TW / 2;
  }

  // Spotlight padding
  const SP = 8;

  return (
    <>
      {/* ── Full-screen dark overlay (click to skip) ── */}
      <div className="tour-overlay" onClick={finish} />

      {/* ── Spotlight: fixed-position cutout ── */}
      {rect && (
        <div
          className="tour-spotlight"
          style={{
            top:    rect.top    - SP,
            left:   rect.left   - SP,
            width:  rect.width  + SP * 2,
            height: rect.height + SP * 2,
          }}
        />
      )}

      {/* ── Tooltip ── */}
      <div
        ref={tooltipRef}
        className={`tour-tooltip${show ? ' tour-tooltip--visible' : ''}`}
        style={{ top: ttTop, left: ttLeft, width: TW }}
        onClick={e => e.stopPropagation()}
      >
        {/* Progress dots */}
        <div className="tour-dots">
          {steps.map((_, i) => (
            <span key={i} className={`tour-dot${i === step ? ' tour-dot--active' : ''}`} />
          ))}
        </div>

        <button className="tour-close" onClick={finish} title="End tour">×</button>

        <div className="tour-icon">{current.icon || '👋'}</div>
        <h3 className="tour-title">{current.title}</h3>
        <p className="tour-body">{current.body}</p>

        <div className="tour-actions">
          {step > 0 && (
            <button className="tour-btn tour-btn--ghost" onClick={goPrev}>← Back</button>
          )}
          <button className="tour-btn tour-btn--skip" onClick={finish}>Skip</button>
          <button className="tour-btn tour-btn--next" onClick={goNext}>
            {step === steps.length - 1 ? "Let's go 🚀" : 'Next →'}
          </button>
        </div>

        <div className="tour-step-count">{step + 1} / {steps.length}</div>
      </div>
    </>
  );
}
